// src/components/admin/blog/CategoriesManagement.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  FolderOpen, 
  Edit, 
  Trash2, 
  Upload, 
  X,
  Search,
  RefreshCw, ArrowLeft,
} from 'lucide-react';


const CategoriesManagement = ({ showNotification }) => {
// const CategoriesManagement = ({ showNotification = () => {} }) => {

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null
  });
  
  // For file upload
  const fileInputRef = useRef(null);
  
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchCategories();
  }, []);
  
  useEffect(() => {
    // Filter categories based on search term
    if (searchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(
        category => 
          category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCategories(filtered);
    }
  }, [categories, searchTerm]);
  
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/blog/categories');
      
      if (!res.ok) throw new Error('Failed to fetch categories');
      
      const data = await res.json();
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showNotification('error', 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch('/api/blog/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }
      
      const data = await res.json();
      
      setFormData(prev => ({
        ...prev,
        image: data.filePath
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      showNotification('error', error.message || 'Failed to upload image');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing 
        ? `/api/blog/categories/${formData._id}`
        : '/api/blog/categories';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          image: formData.image
        })
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `Failed to ${isEditing ? 'update' : 'create'} category`);
      }
      
      // Close modal and reset form
      setIsModalOpen(false);
      setFormData({
        name: '',
        description: '',
        image: null
      });
      setIsEditing(false);
      
      // Refresh categories
      fetchCategories();
      
      showNotification('success', `Category ${isEditing ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} category:`, error);
      showNotification('error', error.message || `Failed to ${isEditing ? 'update' : 'create'} category`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (category) => {
    setFormData({
      _id: category._id,
      name: category.name,
      description: category.description || '',
      image: category.image
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };
  
  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This may affect associated posts.')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/blog/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to delete category');
      }
      
      // Refresh categories
      fetchCategories();
      
      showNotification('success', 'Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification('error', error.message || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          <button 
            onClick={() => navigate('/admin/blog')}
            style={{ 
              boxShadow: 'none', 
              border: 'none', 
              background: 'transparent',
              transform: 'none',
            }}
            className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={24}/>
            </button>
            <span>Category Management</span>
        </h2>
        <button
          onClick={() => {
            setFormData({
              name: '',
              description: '',
              image: null
            });
            setIsEditing(false);
            setIsModalOpen(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Category
        </button>
      </div>
      
      {/* Search & Filter */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search categories..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <button
            onClick={fetchCategories}
            className="ml-3 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Categories Grid */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
            <p className="mt-2 text-gray-500">Loading categories...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No categories found</h3>
            <p className="mt-1 text-gray-500">
              {searchTerm ? 'No categories match your search' : 'Get started by creating a new category'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  setFormData({
                    name: '',
                    description: '',
                    image: null
                  });
                  setIsEditing(false);
                  setIsModalOpen(true);
                }}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Category
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCategories.map(category => (
              <div 
                key={category._id} 
                className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                {category.image ? (
                  <div 
                    className="h-48 bg-cover bg-center" 
                    style={{ backgroundImage: `url(${category.image})` }}
                  ></div>
                ) : (
                  <div className="h-48 bg-gray-100 flex items-center justify-center">
                    <FolderOpen className="h-16 w-16 text-gray-300" />
                  </div>
                )}
                
                <div className="p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">{category.name}</h3>
                  {category.description && (
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                      {category.description}
                    </p>
                  )}
                  
                  <div className="flex justify-between items-center mt-2">
                    <button
                      onClick={() => navigate(`/admin/blog/posts?category=${category.slug}`)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      View Posts
                    </button>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-amber-600 hover:text-amber-800"
                        title="Edit"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(category._id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Category Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsModalOpen(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        {isEditing ? 'Edit Category' : 'Create New Category'}
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-green-500 focus:border-green-500"
                            placeholder="Category name"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-green-500 focus:border-green-500"
                            placeholder="Brief description of the category"
                          ></textarea>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Image
                          </label>
                          
                          {formData.image ? (
                            <div className="relative mt-2">
                              <img
                                src={formData.image}
                                alt="Category"
                                className="w-full h-48 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, image: null }))}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                              <div className="space-y-1 text-center">
                                <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
                                <div className="flex text-sm text-gray-600">
                                  <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none"
                                  >
                                    <span>Upload an image</span>
                                    <input
                                      id="file-upload"
                                      ref={fileInputRef}
                                      type="file"
                                      accept="image/*"
                                      className="sr-only"
                                      onChange={(e) => {
                                        if (e.target.files?.length) {
                                          handleFileUpload(e.target.files[0]);
                                        }
                                      }}
                                    />
                                  </label>
                                  <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">
                                  PNG, JPG, GIF up to 10MB
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      isEditing ? 'Update' : 'Create'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style jsx="true">{`
        button {
          box-shadow: none !important;
          border: none !important;
          transform: none !important;
        }      
       `}</style>
    </div>
  );
};

export default CategoriesManagement;